import React, { useEffect, useState } from 'react';
import { getCompany, updateCompany } from '../utils/api';
import { Building2, Save } from 'lucide-react';
import Layout from '../components/Layout';

const Company = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    try {
      const { data } = await getCompany();
      setCompany(data);
    } catch (error) {
      console.error('Error loading company:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await updateCompany(company);
      setMessage('Company settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout variant="light">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout variant="light">
      <div className="fade-in">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Company Settings</h1>
          <p className="text-light-gray">Manage brand settings and merge fields</p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.includes('Error') ? 'bg-orange/15 text-orange' : 'bg-lime/15 text-lime'}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card mb-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Building2 size={24} />
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-light-gray mb-2">Company Name</label>
                <input
                  type="text"
                  value={company?.name || ''}
                  onChange={(e) => setCompany({ ...company, name: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-light-gray mb-2">Phone Number</label>
                <input
                  type="text"
                  value={company?.phone || ''}
                  onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                  className="input-field"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-light-gray mb-2">Service Areas (comma-separated)</label>
                <input
                  type="text"
                  value={company?.service_areas?.join(', ') || ''}
                  onChange={(e) =>
                    setCompany({
                      ...company,
                      service_areas: e.target.value.split(',').map((s) => s.trim()),
                    })
                  }
                  className="input-field"
                  placeholder="Austin, Dallas, Houston, San Antonio"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-light-gray mb-2">Default Offer</label>
                <input
                  type="text"
                  value={company?.default_offer || ''}
                  onChange={(e) => setCompany({ ...company, default_offer: e.target.value })}
                  className="input-field"
                  placeholder="$99 Service Special - Limited Time!"
                />
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Brand Colors</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-light-gray mb-2">Primary Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={company?.brand_colors?.primary || '#F47C20'}
                    onChange={(e) =>
                      setCompany({
                        ...company,
                        brand_colors: { ...company.brand_colors, primary: e.target.value },
                      })
                    }
                    className="h-12 w-12 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={company?.brand_colors?.primary || '#F47C20'}
                    onChange={(e) =>
                      setCompany({
                        ...company,
                        brand_colors: { ...company.brand_colors, primary: e.target.value },
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="block text-light-gray mb-2">Secondary Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={company?.brand_colors?.secondary || '#0B1F35'}
                    onChange={(e) =>
                      setCompany({
                        ...company,
                        brand_colors: { ...company.brand_colors, secondary: e.target.value },
                      })
                    }
                    className="h-12 w-12 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={company?.brand_colors?.secondary || '#0B1F35'}
                    onChange={(e) =>
                      setCompany({
                        ...company,
                        brand_colors: { ...company.brand_colors, secondary: e.target.value },
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="block text-light-gray mb-2">Accent Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={company?.brand_colors?.accent || '#132A46'}
                    onChange={(e) =>
                      setCompany({
                        ...company,
                        brand_colors: { ...company.brand_colors, accent: e.target.value },
                      })
                    }
                    className="h-12 w-12 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={company?.brand_colors?.accent || '#132A46'}
                    onChange={(e) =>
                      setCompany({
                        ...company,
                        brand_colors: { ...company.brand_colors, accent: e.target.value },
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Merge Fields</h2>
            <p className="text-muted text-sm mb-4">
              These fields auto-populate in campaign assets. Update them here to change across all campaigns.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {company?.merge_fields &&
                Object.entries(company.merge_fields).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-light-gray mb-2">{key}</label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) =>
                        setCompany({
                          ...company,
                          merge_fields: { ...company.merge_fields, [key]: e.target.value },
                        })
                      }
                      className="input-field"
                    />
                  </div>
                ))}
            </div>
          </div>

          <button type="submit" disabled={saving} className="btn-primary">
            <Save className="inline mr-2" size={20} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default Company;
